import {transform} from "ol/proj";
import {getViewProjectionName} from "@/helpers/ol/ol-projection-helper";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import {MultiPoint, Point, Polygon} from "ol/geom";
import {getViewRotation, viewFit} from "@/helpers/ol/ol-view";
import {Feature} from "ol";
import {Modify, Select, Translate} from "ol/interaction";
import {never, platformModifierKeyOnly, primaryAction} from "ol/events/condition";
import {getScaleFromResolution} from "@/helpers/print/print-scales-helper";
import {getCenter, getHeight, getWidth} from "ol/extent";
import {Fill, Stroke, Style} from "ol/style";
import CircleStyle from "ol/style/Circle";
import {removeLayer} from "@/helpers/ol/ol-layer-helper";

let polygon
let feature
let modify
let translate

export function clearWordPrint() {
    removeLayer('wordPrint', false)
    if(modify) {
        window.map.removeInteraction(modify);
        window.map.removeInteraction(translate);
    }
}

export function wordAddRectangle(geometry, width, height) {

    clearWordPrint()

    const resolution = window.map.getView().getResolution()
    const scale = getScaleFromResolution(resolution, 'm')

    const w = width * scale * 0.00035273
    const h = height * scale * 0.00035273

    const mapCenter = getCenter(geometry.getExtent())
    const center = new transform(mapCenter, getViewProjectionName(), "EPSG:28992")

    const points = [];

    points[0] =[center[0] - w / 2, center[1] - h / 2];
    points[1] = [center[0] + w / 2, center[1] - h / 2];
    points[2] = [center[0] + w / 2, center[1] + h / 2];
    points[3] = [center[0] - w / 2, center[1] + h / 2];
    points[4] = [center[0] - w / 2, center[1] - h / 2];

    const vectorSource = new VectorSource()

    const style = new Style({
        geometry: function (feature) {
            const modifyGeometry = feature.get('modifyGeometry');
            return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
        },
        fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
            color: '#ffcc33',
            width: 2,
        }),
        image: new CircleStyle({
            radius: 7,
            fill: new Fill({
                color: '#ffcc33',
            }),
        }),
    });

    const vectorLayer = new VectorLayer({
        altitudeMode: 'clampToGround',
        name: 'wordPrint',
        source: vectorSource,
        zIndex: 1000,
        style: function (feature) {
            const styles = [style];
            const modifyGeometry = feature.get('modifyGeometry');
            const geometry = modifyGeometry
                ? modifyGeometry.geometry
                : feature.getGeometry();
            const result = calculateCenter(geometry);
            const center = result.center;
            if (center) {
                styles.push(
                    new Style({
                        geometry: new Point(center),
                        image: new CircleStyle({
                            radius: 4,
                            fill: new Fill({
                                color: '#ff3333',
                            }),
                        }),
                    })
                );
                const coordinates = result.coordinates;
                if (coordinates) {
                    const minRadius = result.minRadius;
                    const sqDistances = result.sqDistances;
                    const rsq = minRadius * minRadius;
                    const points = coordinates.filter(function (coordinate, index) {
                        return sqDistances[index] > rsq;
                    });
                    styles.push(
                        new Style({
                            geometry: new MultiPoint(points),
                            image: new CircleStyle({
                                radius: 4,
                                fill: new Fill({
                                    color: '#33cc33',
                                }),
                            }),
                        })
                    );
                }
            }
            return styles;
        },
    });


    points.map((point, key) => {
        points[key] = new transform(point, "EPSG:28992", getViewProjectionName())
    })

    polygon = new Polygon([points]);

    const rotateAngle = getViewRotation();
    polygon.rotate( rotateAngle, window.map.getView().getCenter());

    feature = new Feature({
        name: "Thing",
        geometry: polygon
    });
    vectorSource.addFeature(feature)

    window.map.addLayer(vectorLayer)

    const defaultStyle = new Modify({source: vectorSource})
        .getOverlay()
        .getStyleFunction();

    modify = new Modify({
        source: vectorSource,
        condition: function (event) {
            return primaryAction(event) && !platformModifierKeyOnly(event);
        },
        deleteCondition: never,
        insertVertexCondition: never,
        style: function (feature) {
            feature.get('features').forEach(function (modifyFeature) {
                const modifyGeometry = modifyFeature.get('modifyGeometry');
                if (modifyGeometry) {
                    const point = feature.getGeometry().getCoordinates();
                    let modifyPoint = modifyGeometry.point;
                    if (!modifyPoint) {
                        // save the initial geometry and vertex position
                        modifyPoint = point;
                        modifyGeometry.point = modifyPoint;
                        modifyGeometry.geometry0 = modifyGeometry.geometry;
                        // get anchor and minimum radius of vertices to be used
                        const result = calculateCenter(modifyGeometry.geometry0);
                        modifyGeometry.center = result.center;
                        modifyGeometry.minRadius = result.minRadius;
                    }

                    const center = modifyGeometry.center;
                    const minRadius = modifyGeometry.minRadius;
                    let dx, dy;
                    dx = modifyPoint[0] - center[0];
                    dy = modifyPoint[1] - center[1];
                    const initialRadius = Math.sqrt(dx * dx + dy * dy);
                    if (initialRadius > minRadius) {
                        const initialAngle = Math.atan2(dy, dx);
                        dx = point[0] - center[0];
                        dy = point[1] - center[1];
                        const currentRadius = Math.sqrt(dx * dx + dy * dy);
                        if (currentRadius > 0) {
                            const currentAngle = Math.atan2(dy, dx);
                            const geometry = modifyGeometry.geometry0.clone();
                            geometry.scale(currentRadius / initialRadius, undefined, center);
                            // geometry.rotate(currentAngle - initialAngle, center);
                            modifyGeometry.geometry = geometry;
                        }
                    }
                }
            });
            return defaultStyle(feature);
        },
    });

    modify.on('modifystart', function (event) {
        event.features.forEach(function (feature) {
            feature.set(
                'modifyGeometry',
                {geometry: feature.getGeometry().clone()},
                true
            );
        });
    });

    modify.on('modifyend', function (event) {
        event.features.forEach(function (feature) {
            const modifyGeometry = feature.get('modifyGeometry');
            if (modifyGeometry) {
                feature.setGeometry(modifyGeometry.geometry);
                feature.unset('modifyGeometry', true);
            }
        });
    });

    translate = new Translate({
        condition: function (event) {
            return primaryAction(event) && platformModifierKeyOnly(event);
        },
        layers: [vectorLayer],
    })

    map.addInteraction(modify);
    map.addInteraction(translate);
}

function calculateCenter(geometry) {
    let center, coordinates, minRadius;
    const type = geometry.getType();
    if (type === 'Polygon') {
        let x = 0;
        let y = 0;
        let i = 0;
        coordinates = geometry.getCoordinates()[0].slice(1);
        coordinates.forEach(function (coordinate) {
            x += coordinate[0];
            y += coordinate[1];
            i++;
        });
        center = [x / i, y / i];
    } else if (type === 'LineString') {
        center = geometry.getCoordinateAt(0.5);
        coordinates = geometry.getCoordinates();
    } else {
        center = getCenter(geometry.getExtent());
    }
    let sqDistances;
    if (coordinates) {
        sqDistances = coordinates.map(function (coordinate) {
            const dx = coordinate[0] - center[0];
            const dy = coordinate[1] - center[1];
            return dx * dx + dy * dy;
        });
        minRadius = Math.sqrt(Math.max.apply(Math, sqDistances)) / 3;
    } else {
        minRadius =
            Math.max(
                getWidth(geometry.getExtent()),
                getHeight(geometry.getExtent())
            ) / 3;
    }
    return {
        center: center,
        coordinates: coordinates,
        minRadius: minRadius,
        sqDistances: sqDistances,
    };
}
